import { 
  doc, 
  setDoc, 
  updateDoc, 
  getDoc, 
  deleteDoc,
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Business, SignUpFormData, User as AppUser } from '@/types/user';
import { AuthService } from './auth.service';

/**
 * User Service - Handles all Firestore user operations
 */
export class UserService {
  /**
   * Get user document from Firestore
   * @param uid User ID
   */
  static async getUser(uid: string): Promise<AppUser | null> {
    const userDoc = await getDoc(doc(db, 'users', uid));
    return userDoc.exists() ? (userDoc.data() as AppUser) : null;
  }

  /**
   * Check if phone number already exists in Firestore
   * @param phone Phone number to check
   */
  static async checkPhoneExists(phone: string): Promise<boolean> {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('phone', '==', phone));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  }

  /**
   * Create new user document in Firestore
   * @param firstName User's first name
   * @param lastName User's last name
   * @param email User's email
   * @param uid User ID from Firebase Auth
   * @param phone User's phone number (optional)
   */
  static async createUser(
    firstName: string,
    lastName: string,
    email: string,
    uid: string,
    businessId: string,
    phone?: string
  ): Promise<void> {
    const appUser: AppUser = {
      uid,
      firstName,
      lastName,
      email,
      businessId,
      phone,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(doc(db, 'users', uid), appUser);
  }

  /**
   * Create a business document if it does not already exist.
   * The business name is used as the Firestore document ID.
   * @param name Business name
   * @param address Business address
   */
  static async createBusinessIfMissing(name: string, address: string): Promise<void> {
    const businessId = name.trim();
    const businessRef = doc(db, 'businesses', businessId);
    const businessDoc = await getDoc(businessRef);

    if (businessDoc.exists()) return;

    const business: Business = {
      id: businessId,
      name: businessId,
      address: address.trim(),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(businessRef, business);
  }

  /**
   * Update user document in Firestore
   * @param uid User ID
   * @param userData Partial user data to update
   */
  static async updateUser(uid: string, userData: Partial<AppUser>): Promise<void> {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: new Date(),
    });
  }

  /**
   * Complete user registration process
   * @param firstName User's first name
   * @param lastName User's last name
   * @param email User's email
   * @param password User's password
   * @param phone User's phone number (optional)
   */
  static async registerUser(formData: SignUpFormData, phone?: string) {
    // 1. Create Firebase auth user
    const userCredential = await AuthService.createUserWithoutSwitchingSession(
      formData.email,
      formData.password,
      `${formData.firstName} ${formData.lastName}`
    );
    const user = userCredential.user;

    // 2. Create business document if this name is not already registered
    await this.createBusinessIfMissing(formData.businessName, formData.businessAddress);

    // 3. Create user document in Firestore
    await this.createUser(
      formData.firstName,
      formData.lastName,
      formData.email,
      user.uid,
      formData.businessName.trim(),
      phone
    );

    // 4. Sign out user so they need to manually sign in
    // await AuthService.signOut();

    return userCredential;
  }

  /**
   * Delete user document from Firestore
   * @param uid User ID
   */
  static async deleteUser(uid: string): Promise<void> {
    const userRef = doc(db, 'users', uid);
    await deleteDoc(userRef);
  }
}
